import { useLastPrivateAgent } from "./hooks/useLastPrivateAgent";
import type {
  ActivitySourceDetailResponse,
  AgentChatMessage as AgentChatThreadMessage,
  AgentChatResponse,
  GraphEdge,
  GraphCanonicalEntity,
  GraphEntityType,
  GraphFrameCommitResponse,
  GraphFrameRecord,
  GraphNoteIntakeResponse,
  GraphNode,
  GraphOpportunityActionRequest,
  GraphReadNode,
  GraphVisualizationFrame,
  TransactionDetailResponse,
  TransactionGraphResponse
} from "@salesko/contracts";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { X } from "lucide-react";
import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useI18n, type Dictionary } from "../i18n";
import {
  agentChatStreamEndpoint,
  agentProviderAuthHeaders,
  clearStoredSaleskoSession,
  commitGraphFrame,
  createNoteIntake,
  deleteAgentChatThread,
  getActivitySourceDetail,
  getGraphFrame,
  getGraphOverviewProjection,
  getGraphRelationshipPaths,
  getSaleskoSchedule,
  getTransactionActivityCapability,
  getTransactionDetail,
  getTransactionGraph,
  getNoteIntakeJob,
  listActivityFeed,
  listAgentChatThreads,
  listGraphOpportunities,
  listGraphFrames,
  listCanonicalEntities,
  listGraphRelationshipReminders,
  readAgentChatThread,
  readSharedGraphFrame,
  saveAgentChatThread,
  searchGraphNodes,
  updateGraphOpportunity
} from "../lib/api";
import { AgentOverlay } from "./components/AgentOverlay";
import {
  privateAgentComputerAvailability,
  privateAgentReadinessComputerAvailability
} from "./components/PrivateAgentJobsPanel";
import { PrivateAgentWorkspace } from "./components/PrivateAgentWorkspace";
import {
  PrivateAgentDetailTabs,
  type PrivateAgentDetailTab,
  type PrivateAgentTouchedEntityRow
} from "./components/PrivateAgentDetailTabs";
import { PrivateAgentSelector } from "./components/PrivateAgentSelector";
import {
  PrivateAgentResearchTrigger,
  privateAgentResearchBlockReason
} from "./components/PrivateAgentResearchTrigger";
import { CurrentLeadsView } from "./CurrentLeadsView";
import { MobileWorkspace } from "./mobile/MobileWorkspace";
import { useIsMobile, useWorkspaceMediaQuery } from "./hooks/useIsMobile";
import { useInstallPrompt } from "../lib/pwa";
import { CanonicalEntityRegistryPanel } from "./components/CanonicalEntityRegistryPanel";
import { WorkspaceShell } from "./components/WorkspaceShell";
import { WorkspaceSidePanel } from "./components/WorkspaceSidePanel";
import { DetailPanel } from "./components/DetailPanel";
import { GraphStage } from "./components/GraphStage";
import { LeftRail } from "./components/LeftRail";
import { OnboardingPanel } from "./components/OnboardingPanel";
import { SignalsPanel } from "./components/SignalsPanel";
import { TransactionGraphDialog } from "./components/TransactionGraphDialog";
import type { ActivityFeedPanelProps, ActivitySourceSelection } from "./components/ActivityFeedPanel";
import {
  SchedulePanel,
  scheduleQueryKey,
  scheduleRangeForLocalDays,
  workspaceViewAfterFrameUpdate
} from "./components/SchedulePanel";
import { WorkspaceHeader } from "./components/WorkspaceHeader";
import { useAgentSession } from "./hooks/useAgentSession";
import { useCanonicalIdentityActions } from "./hooks/useCanonicalIdentityActions";
import { useGraphChangeSets } from "./hooks/useGraphChangeSets";
import { newAgentResearchClientRequestId, usePrivateAgentJobs } from "./hooks/usePrivateAgentJobs";
import { useGraphFrameIo } from "./hooks/useGraphFrameIo";
import { useWorkspaceFrameState } from "./hooks/useWorkspaceFrameState";
import { defaultGraphFrame, sampleText } from "./fixtures/defaultGraphFrame";
import { createAgentChatInput, noteIntakeExtractionFailureMessage, resolveSeekerTurnResult } from "./lib/agentChat";
import { isAgentChatThreadNotFoundError } from "./lib/agentThreadRestore";
import { readStoredActiveThreadId, writeStoredActiveThreadId } from "./lib/agentThreadStorage";
import { leadStageToggleChangeSetWrite } from "./lib/leadStageChangeSet";
import type { NodeEditDraft } from "./lib/nodeEdit";
import { nodeEditChangeSetWrite } from "./lib/nodeEdit";
import { latestFrameIdToRestore } from "./lib/frameRestore";
import type { RelationshipCreateDraft } from "./lib/relationshipCreate";
import {
  relationshipCreateChangeSetWrite,
  relationshipDeleteChangeSetWrite,
  relationshipEditChangeSetWrite,
  relationshipTouchpointChangeSetWrite,
  type RelationshipEditDraft
} from "./lib/relationshipCreate";
import { graphSearchText } from "./lib/search";
import { savedFrameSearchResults } from "./lib/serverSearch";
import { stableReviewCommitMessage } from "./lib/stableText";
import { timelineReplayChangeSetWrite } from "./lib/timeline";
import { markFirstRunOnboardingDismissed, shouldShowFirstRunOnboarding } from "./lib/onboarding";
import { overviewEntityIdFromNodeId, overviewFrameFromProjection } from "./lib/overviewFrame";
import {
  canonicalRegistryEntityTypeForView,
  canonicalRegistryViewForDetailTab,
  isCanonicalRegistryView
} from "./lib/canonicalRegistry";
import { canonicalEntityIdForGraphNode } from "./lib/canonicalIdentity";
import { privateAgentTouchedEntitySelection } from "./lib/privateAgentTouchedEntitySelection";
import {
  initialWorkspaceView,
  isWorkspaceViewId,
  type PrivateAgentWorkspaceFacet,
  type WorkspaceViewId
} from "./navItems";
import {
  initialSearchSelectionState,
  searchSelectionNotice,
  searchSelectionTransition,
  viewAfterNodeSelect,
  type SearchSelectionEvent
} from "./searchSelectionNavigation";
import { activityFeedPagesProjection } from "./lib/activityFeed";
import { overviewProjectionQueryKey } from "./lib/overviewProjectionQuery";
import type {
  AgentChatMessage,
  AgentChatMode,
  AgentMutationVariables,
  AgentChatResult,
  DetailTab,
  MentionQuery,
  RelationshipIntelligenceState
} from "./types";

const appRoute = getRouteApi("/app");
const relationshipPathLimit = 4;
const relationshipReminderLimit = 12;
const relationshipReminderStaleAfterDays = 90;
const noteIntakePollAttempts = 20;
const noteIntakePollIntervalMs = 1000;

function stringDataValue(value: unknown) {
  return typeof value === "string" && value.length > 0 ? value : null;
}

const tenantScopedQueryKeyRoots = new Set([
  "private-agents",
  "private-agent-profile",
  "private-agent-readiness",
  "private-agent-chat-conversations",
  "private-agent-chat-conversation",
  "private-agent-chat-conversation-identity",
  "private-agent-chat-events",
  "agent-research-jobs",
  "agent-research-entitlement",
  "agent-research-job",
  "agent-threads",
  "activity-feed",
  "activity-source-detail",
  "transaction-graph",
  "graph-canonical-entities",
  "graph-frame",
  "graph-frames",
  "graph-node-search",
  "graph-opportunities",
  "graph-overview-projection",
  "graph-relationship-paths",
  "graph-relationship-reminders",
  "schedule",
  "tenant-agent-settings",
  "tenant-members",
  "tenant-memberships",
  "tenant-policy",
  "transaction-activity-capability",
  "transaction-detail"
]);

const initialAgentMessages: AgentChatMessage[] = [
  {
    id: "agent-welcome",
    role: "assistant",
    status: "done",
    content:
      "Tell me what happened. I will turn it into a relationship graph and call out people, events, companies, and opportunities. Type @ to mention current graph entities."
  }
];

type ReviewFrameActionVariables = {
  action: "accepted" | "discarded";
  frame: GraphVisualizationFrame;
  nodeLabel: string;
  previous: {
    commit: GraphFrameCommitResponse;
    frame: GraphVisualizationFrame;
  };
};

type OpportunityActionVariables = {
  action: GraphOpportunityActionRequest["action"];
  id: string;
};

export function shouldOpenFirstRunOnboarding(input: {
  explicitView: WorkspaceViewId | undefined;
  isSharedReadOnly: boolean;
  onboardingAvailable: boolean;
}) {
  return !input.explicitView && !input.isSharedReadOnly && input.onboardingAvailable;
}

export function IntakeRoute() {
  const { t } = useI18n();
  const search = appRoute.useSearch();
  const navigate = appRoute.useNavigate();
  const queryClient = useQueryClient();
  const { agentAuthLabel, availableAuthProviders, handleAuthProviderSignIn, storedSession } = useAgentSession();
  const hasStoredSession = Boolean(storedSession);
  const activeView = search.view ?? initialWorkspaceView(hasStoredSession);
  const isPrivateAgentWorkspace = activeView === "privateAgent";
  const privateAgentFacet = search.privateAgentFacet ?? "chat";
  // Which side-panel tab the Private Agent view shows. It is deliberately local
  // and not in the URL: it is a reading position, not a place to return to. A
  // new turn never moves it; only opening an entity does, and that moves it back
  // to the detail the click just opened.
  const [privateAgentDetailTab, setPrivateAgentDetailTab] = useState<PrivateAgentDetailTab>("detail");
  const [lastPrivateAgentId, rememberPrivateAgent] = useLastPrivateAgent();
  const setPrivateAgentSelection = useCallback((agentId: string | undefined) => {
    rememberPrivateAgent(agentId);
    void navigate({ search: (current) => {
      const { agent: _agent, privateAgentConversationId: _conversation, ...rest } = current;
      return agentId ? { ...rest, agent: agentId } : rest;
    } });
  }, [navigate, rememberPrivateAgent]);
  const setActiveView = useCallback(
    (view: WorkspaceViewId) => {
      void navigate({
        search: (current) => {
          if (view === "privateAgent") {
            return { ...current, view };
          }

          const {
            privateAgentConversationId: _privateAgentConversationId,
            privateAgentFacet: _privateAgentFacet,
            ...withoutPrivateAgentChatState
          } = current;
          return { ...withoutPrivateAgentChatState, view };
        }
      });
    },
    [navigate]
  );
  const setPrivateAgentFacet = useCallback(
    (facet: PrivateAgentWorkspaceFacet) => {
      void navigate({
        search: (current) => {
          if (facet !== "chat") return { ...current, privateAgentFacet: facet, view: "privateAgent" };
          const { privateAgentFacet: _privateAgentFacet, ...withoutFacet } = current;
          return { ...withoutFacet, view: "privateAgent" };
        }
      });
    },
    [navigate]
  );
  const setPrivateAgentConversation = useCallback(
    (conversationId: string | undefined, agentId?: string) => {
      if (agentId) rememberPrivateAgent(agentId);
      void navigate({
        search: (current) => {
          if (conversationId) {
            return { ...current, ...(agentId ? { agent: agentId } : {}), privateAgentConversationId: conversationId, view: "privateAgent" };
          }
          const { privateAgentConversationId: _privateAgentConversationId, ...withoutConversation } = current;
          return { ...withoutConversation, view: "privateAgent" };
        }
      });
    },
    [navigate, rememberPrivateAgent]
  );
  // Persist the active thread id across reloads/devices (design: localStorage, not the
  // URL — `?frame=` already owns the search string). A stored id is resumed below once
  // sign-in state is known; an absent one is minted fresh and written back immediately
  // so a save before the first reload still targets a recoverable thread id.
  const [threadId, setThreadId] = useState(() => {
    const stored = readStoredActiveThreadId();
    if (stored) {
      return stored;
    }

    const freshThreadId = chatMessageId("thread");
    writeStoredActiveThreadId(freshThreadId);
    return freshThreadId;
  });
  const [activeThreadRestoreAttempted, setActiveThreadRestoreAttempted] = useState(false);
  const [activeThreadRestoreErrorId, setActiveThreadRestoreErrorId] = useState<string | null>(null);
  const [threadSaveError, setThreadSaveError] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [chatMessages, setChatMessages] = useState<AgentChatMessage[]>(initialAgentMessages);
  const [agentOpen, setAgentOpen] = useState(false);
  // Writer (graph-copilot, default) vs seeker (read-only discussion) — a live composer
  // toggle, not per-thread state: switching threads does not reset it.
  const [agentMode, setAgentMode] = useState<AgentChatMode>("graph-copilot");
  const detailDrawer = useWorkspaceMediaQuery("(max-width: 900px)");
  const [detailPanelOpen, setDetailPanelOpen] = useState(() => activeView !== "privateAgent" || !detailDrawer);
  useEffect(() => {
    if (activeView === "privateAgent") setDetailPanelOpen(!detailDrawer);
  }, [activeView, detailDrawer]);
  const isMobile = useIsMobile();
  const install = useInstallPrompt();
  const [firstRunOnboardingChecked, setFirstRunOnboardingChecked] = useState(false);
  const [firstRunOnboardingActive, setFirstRunOnboardingActive] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<MentionQuery | null>(null);
  // Cross-frame jump state ("still jumping" vs "jump failed"). searchSelectionTransition
  // is the only thing that decides it; this component just applies the result and runs
  // the side effect it asks for.
  const [searchSelection, setSearchSelection] = useState(initialSearchSelectionState);
  const applySearchSelectionEvent = (event: SearchSelectionEvent) => {
    setSearchSelection((current) => searchSelectionTransition(current, event).state);
  };
  const [selectedActivitySource, setSelectedActivitySource] = useState<ActivitySourceSelection | null>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [selectedTransactionGraphId, setSelectedTransactionGraphId] = useState<string | null>(null);
  const [relationshipPathTargetId, setRelationshipPathTargetId] = useState("");
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const agentThreadRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const suppressMentionEscapeKeyUpRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Set synchronously in the chat mutation's onSuccess; consumed by the effect below.
  // A save must read chatMessages *after* React has actually committed the "done" flip,
  // which a plain state-updater capture cannot guarantee (updater invocation timing is
  // not part of React's public contract) — so the effect, not onSuccess, does the read.
  const pendingThreadSaveRef = useRef(false);
  const {
    acceptSelectedReview,
    detailTab,
    detailTabs,
    discardSelectedReview,
    enabledEntityTypes,
    filters,
    frame,
    handleRecenter,
    handleSelectEdge,
    handleSelectNode,
    handleViewModeChange,
    handleZoomIn,
    handleZoomOut,
    lastResult,
    searchQuery,
    searchResultIds,
    searchResults,
    selectedEdgeId,
    selectedNode,
    selectedNodeId,
    selfNodeId,
    setClampedZoomLevel,
    setSelfNode,
    clearSelfNode,
    clearSelection,
    setDetailTab,
    setFilters,
    setSearchQuery,
    setTimelineFocus,
    spotlightNodeIds,
    timeline,
    timelineFocus,
    updateWorkspaceFrame,
    viewMode,
    visibleLinkedNodes,
    zoomLevel
  } = useWorkspaceFrameState(defaultGraphFrame);
  const isSharedReadOnly = Boolean(search.share);
  const mutableFrameRecordId = isSharedReadOnly ? null : lastResult?.commit.id ?? null;
  const searchTerm = searchQuery.trim();
  const serverSearchEntityType = enabledEntityTypes.length === 1 ? enabledEntityTypes[0] : undefined;
  const canonicalRegistryVisible = isCanonicalRegistryView(activeView);
  const canonicalRegistryEntityType = canonicalRegistryEntityTypeForView(activeView);
  const canonicalRegistryLimit = activeView === "overview" ? 50 : 24;
  const canReadCanonicalRegistry = canonicalRegistryVisible && !isSharedReadOnly && hasStoredSession;
  const canReadOverviewProjection = activeView === "overview" && !isSharedReadOnly && hasStoredSession;
  const canReadOpportunitySignals = !isSharedReadOnly && hasStoredSession && (activeView === "signals" || isMobile);
  const canReadTransactionActivityCapability = !isSharedReadOnly && hasStoredSession;
  const canReadSchedule = !isMobile && !isSharedReadOnly && hasStoredSession && activeView === "schedule";
  const scheduleRange = useMemo(() => scheduleRangeForLocalDays(), []);
  const scheduleTenantId = storedSession?.tenantId ?? null;
  const activeCanonicalEntityId = selectedNode ? canonicalEntityIdForGraphNode(selectedNode) : null;

  const transactionActivityCapabilityQuery = useQuery({
    queryKey: ["transaction-activity-capability", storedSession?.tenantId ?? null],
    queryFn: getTransactionActivityCapability,
    enabled: canReadTransactionActivityCapability,
    staleTime: 60_000,
    retry: 1
  });
  const transactionActivityEnabled = transactionActivityCapabilityQuery.data?.enabled === true;
  const canReadActivityFeed = canReadOpportunitySignals && transactionActivityEnabled;

  // Committed-frame history (left rail) + ?frame= restore. Both read the API list/read
  // routes that previously had no web consumer, so committed frames survive a reload.
  const frameListQuery = useQuery({
    queryKey: ["graph-frames"],
    queryFn: () => listGraphFrames(8),
    enabled: !isSharedReadOnly && hasStoredSession,
    staleTime: 30_000,
    retry: 1
  });
  // Generic Agent chat-thread history (left rail). Mirrors frameListQuery's tenant
  // gating/staleness so the conversation list follows the same read pattern as
  // committed frames; apps/api owns the persistence, this is a pure list read.
  const threadListQuery = useQuery({
    queryKey: ["agent-threads"],
    queryFn: () => listAgentChatThreads(8),
    enabled: !isSharedReadOnly && hasStoredSession,
    staleTime: 30_000,
    retry: 1
  });
  const frameRestoreQuery = useQuery({
    queryKey: ["graph-frame", search.frame],
    queryFn: () => getGraphFrame(search.frame as string),
    enabled: !isSharedReadOnly && hasStoredSession && Boolean(search.frame) && search.frame !== lastResult?.commit.id,
    staleTime: 60_000,
    retry: 1
  });
  const sharedFrameQuery = useQuery({
    queryKey: ["graph-frame-share", search.share],
    queryFn: () => readSharedGraphFrame(search.share as string),
    enabled: isSharedReadOnly,
    staleTime: 60_000,
    retry: 1
  });
  const graphNodeSearchQuery = useQuery({
    queryKey: ["graph-node-search", searchTerm, serverSearchEntityType ?? "all"],
    queryFn: () =>
      searchGraphNodes({
        ...(serverSearchEntityType ? { entityType: serverSearchEntityType } : {}),
        limit: 8,
        query: searchTerm
      }),
    enabled: !isSharedReadOnly && hasStoredSession && enabledEntityTypes.length > 0 && searchTerm.length > 0,
    staleTime: 30_000,
    retry: 1
  });
  const canonicalRegistryQuery = useQuery({
    queryKey: ["graph-canonical-entities", canonicalRegistryEntityType ?? "all", searchTerm, canonicalRegistryLimit],
    queryFn: () =>
      listCanonicalEntities({
        ...(canonicalRegistryEntityType ? { entityType: canonicalRegistryEntityType } : {}),
        limit: canonicalRegistryLimit,
        query: searchTerm
      }),
    enabled: canReadCanonicalRegistry,
    staleTime: 30_000,
    retry: 1
  });
  const overviewProjectionQuery = useQuery({
    queryKey: overviewProjectionQueryKey(storedSession?.tenantId),
    queryFn: getGraphOverviewProjection,
    enabled: canReadOverviewProjection,
    staleTime: 30_000,
    retry: 1
  });
  const overviewFrame = useMemo(
    () => (activeView === "overview" ? overviewFrameFromProjection(overviewProjectionQuery.data) : null),
    [activeView, overviewProjectionQuery.data]
  );
  const displayFrame = overviewFrame ?? frame;
  const opportunityFeedQuery = useQuery({
    queryKey: ["graph-opportunities"],
    queryFn: () => listGraphOpportunities({ limit: 12 }),
    enabled: canReadOpportunitySignals,
    staleTime: 30_000,
    retry: 1
  });
  const activityFeedQuery = useInfiniteQuery({
    queryKey: ["activity-feed", storedSession?.tenantId ?? null],
    queryFn: ({ pageParam }) =>
      listActivityFeed({
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 20
      }),
    enabled: canReadActivityFeed,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: null as string | null,
    staleTime: 30_000,
    retry: 1
  });
  const activityFeedProjection = useMemo(
    () => activityFeedPagesProjection(activityFeedQuery.data?.pages ?? []),
    [activityFeedQuery.data?.pages]
  );
  const activityFeedItems = activityFeedProjection.items;
  const activitySourceDetailQuery = useQuery<ActivitySourceDetailResponse>({
    queryKey: [
      "activity-source-detail",
      storedSession?.tenantId ?? null,
      selectedActivitySource?.entryId ?? null,
      selectedActivitySource?.sourceReferenceHash ?? null
    ],
    queryFn: () =>
      getActivitySourceDetail(
        selectedActivitySource!.entryId,
        selectedActivitySource!.sourceReferenceHash
      ),
    enabled: canReadActivityFeed && selectedActivitySource !== null,
    staleTime: 60_000,
    retry: 0
  });
  const transactionDetailQuery = useQuery<TransactionDetailResponse>({
    queryKey: ["transaction-detail", storedSession?.tenantId ?? null, selectedTransactionId],
    queryFn: () => getTransactionDetail(selectedTransactionId!),
    enabled: canReadActivityFeed && selectedTransactionId !== null,
    staleTime: 60_000,
    retry: 0
  });
  const transactionGraphQuery = useQuery<TransactionGraphResponse>({
    queryKey: ["transaction-graph", storedSession?.tenantId ?? null, selectedTransactionGraphId],
    queryFn: () => getTransactionGraph(selectedTransactionGraphId!),
    enabled: canReadActivityFeed && selectedTransactionGraphId !== null,
    staleTime: 60_000,
    retry: 0
  });
  useEffect(() => {
    if (canReadActivityFeed) {
      return;
    }
    setSelectedActivitySource(null);
    setSelectedTransactionId(null);
    setSelectedTransactionGraphId(null);
  }, [canReadActivityFeed]);
  const scheduleQuery = useQuery({
    queryKey: scheduleQueryKey(scheduleTenantId, scheduleRange),
    queryFn: () => getSaleskoSchedule(scheduleRange),
    enabled: canReadSchedule && scheduleTenantId !== null,
    staleTime: 60_000,
    retry: 1
  });
  const savedSearchResults = useMemo(
    () =>
      savedFrameSearchResults(graphNodeSearchQuery.data?.items ?? [], {
        currentFrameRecordId: mutableFrameRecordId,
        currentSearchResultIds: searchResultIds,
        enabledEntityTypes
      }),
    [enabledEntityTypes, graphNodeSearchQuery.data?.items, mutableFrameRecordId, searchResultIds]
  );
  const latestFrameRestoreId = latestFrameIdToRestore({
    frameItems: frameListQuery.data?.items,
    hasStoredSession,
    isSharedReadOnly,
    loadedFrameRecordId: lastResult?.commit.id ?? null,
    searchFrameId: search.frame
  });
  const relationshipPathTargetNodes = useMemo(
    () => frame.graph.nodes.filter((candidate) => candidate.id !== selectedNodeId),
    [frame.graph.nodes, selectedNodeId]
  );
  const relationshipPathTargetNodeId = useMemo(() => {
    if (relationshipPathTargetNodes.some((candidate) => candidate.id === relationshipPathTargetId)) {
      return relationshipPathTargetId;
    }

    return relationshipPathTargetNodes[0]?.id ?? "";
  }, [relationshipPathTargetId, relationshipPathTargetNodes]);
  const canReadRelationshipReminders = !isSharedReadOnly && hasStoredSession && Boolean(mutableFrameRecordId);
  const canQueryRelationshipIntelligence = canReadRelationshipReminders && Boolean(selectedNodeId);
  const relationshipPathsQuery = useQuery({
    queryKey: ["graph-relationship-paths", mutableFrameRecordId, selectedNodeId, relationshipPathTargetNodeId],
    queryFn: () =>
      getGraphRelationshipPaths(selectedNodeId ?? "", relationshipPathTargetNodeId, {
        limit: relationshipPathLimit
      }),
    enabled: canQueryRelationshipIntelligence && relationshipPathTargetNodeId.length > 0,
    staleTime: 30_000,
    retry: 1
  });
  const relationshipRemindersQuery = useQuery({
    queryKey: ["graph-relationship-reminders", mutableFrameRecordId, relationshipReminderStaleAfterDays],
    queryFn: () =>
      listGraphRelationshipReminders({
        limit: relationshipReminderLimit,
        staleAfterDays: relationshipReminderStaleAfterDays
      }),
    enabled: canReadRelationshipReminders,
    staleTime: 60_000,
    retry: 1
  });
  const selectedRelationshipReminders = useMemo(() => {
    if (!selectedNodeId) {
      return [];
    }

    return (relationshipRemindersQuery.data?.items ?? []).filter(
      (item) => item.edge.source === selectedNodeId || item.edge.target === selectedNodeId
    );
  }, [relationshipRemindersQuery.data?.items, selectedNodeId]);
  const relationshipIntelligence = useMemo<RelationshipIntelligenceState>(() => {
    const pathStatus =
      !canQueryRelationshipIntelligence || relationshipPathTargetNodeId.length === 0
        ? "idle"
        : relationshipPathsQuery.isFetching
          ? "loading"
          : relationshipPathsQuery.isError
            ? "error"
            : relationshipPathsQuery.data
              ? "success"
              : "idle";
    const reminderStatus = !canQueryRelationshipIntelligence
      ? "idle"
      : relationshipRemindersQuery.isFetching
        ? "loading"
        : relationshipRemindersQuery.isError
          ? "error"
          : relationshipRemindersQuery.data
            ? "success"
            : "idle";

    return {
      canQuery: canQueryRelationshipIntelligence,
      pathError:
        relationshipPathsQuery.error instanceof Error ? relationshipPathsQuery.error.message : undefined,
      paths: relationshipPathsQuery.data?.paths ?? [],
      pathStatus,
      reminderError:
        relationshipRemindersQuery.error instanceof Error ? relationshipRemindersQuery.error.message : undefined,
      reminders: selectedRelationshipReminders,
      reminderStatus,
      staleAfterDays: relationshipReminderStaleAfterDays,
      targetNodeId: relationshipPathTargetNodeId,
      targetNodes: relationshipPathTargetNodes,
      tenantReminderCount: relationshipRemindersQuery.data?.count ?? 0
    };
  }, [
    canQueryRelationshipIntelligence,
    relationshipPathTargetNodeId,
    relationshipPathTargetNodes,
    relationshipPathsQuery.data,
    relationshipPathsQuery.error,
    relationshipPathsQuery.isError,
    relationshipPathsQuery.isFetching,
    relationshipRemindersQuery.data,
    relationshipRemindersQuery.error,
    relationshipRemindersQuery.isError,
    relationshipRemindersQuery.isFetching,
    selectedRelationshipReminders
  ]);

  const invalidateFrameQueries = () => {
    void queryClient.invalidateQueries({ queryKey: ["graph-frames"] });
    void queryClient.invalidateQueries({ queryKey: ["graph-frame"] });
    void queryClient.invalidateQueries({ queryKey: ["graph-node-search"] });
    void queryClient.invalidateQueries({ queryKey: ["graph-canonical-entities"] });
    void queryClient.invalidateQueries({ queryKey: ["graph-relationship-paths"] });
    void queryClient.invalidateQueries({ queryKey: ["graph-relationship-reminders"] });
    void queryClient.invalidateQueries({ queryKey: ["graph-opportunities"] });
  };

  const invalidateThreadQueries = () => {
    void queryClient.invalidateQueries({ queryKey: ["agent-threads"] });
  };

  const {
    adoptPendingChangeSet,
    applyChangeSet,
    changeSetAuditExportState,
    changeSetAuditLogState,
    changeSetHistoryState,
    changeSetState,
    createChangeSetPending,
    createPendingChangeSet,
    dismissChangeSetLifecycle,
    exportChangeSetAudit,
    inspectChangeSetAudit,
    refreshChangeSets,
    resetChangeSetContext,
    resetChangeSetLifecycle,
    revertChangeSet
  } = useGraphChangeSets({
    copy: t.changeSet,
    frameRecordId: mutableFrameRecordId,
    onFrameMutated: (nextFrame, commit) => {
      updateWorkspaceFrame(nextFrame, commit);
      setDetailPanelOpen(true);
    },
    onInvalidateFrames: invalidateFrameQueries
  });

  // Private Agent product surface. The jobs list, the tenant's agent profile,
  // its computer and the entitlement all keep their own single sources; this is
  // only the place the workspace reads them together.
  const canUsePrivateAgent = !isSharedReadOnly && hasStoredSession;
  // Canonical ownership is resolved by the selected Agent, never by a retired conversation lookup.
  const selectedAgentId = search.agent ?? (search.privateAgentConversationId ? undefined : lastPrivateAgentId);
  const privateAgentNameRef = useRef<string | null>(null);
  const privateAgent = usePrivateAgentJobs({
    agentId: selectedAgentId,
    enabled: canUsePrivateAgent,
    onChangeSetMaterialized: (response) => {
      // The materialised change set enters the existing review lifecycle
      // unchanged: pending banner, apply action, history refresh.
      adoptPendingChangeSet(
        response,
        t.privateAgentJobs.changeSetSourceLabel(privateAgentNameRef.current ?? t.changeSetHistory.privateAgentSource)
      );
      setDetailPanelOpen(true);
    }
  });
  const privateAgentName = privateAgent.profile?.name ?? null;
  useEffect(() => {
    privateAgentNameRef.current = privateAgentName;
  }, [privateAgentName]);
  // The status dot prefers the server's readiness verdict and only falls back to
  // the local presence projection while that verdict has not been read yet.
  const privateAgentComputer = privateAgent.readiness
    ? privateAgentReadinessComputerAvailability(privateAgent.readiness)
    : privateAgentComputerAvailability(privateAgent.placement, privateAgent.devices, privateAgent.presenceHints);

  const {
    canonicalIdentityPending,
    canonicalIdentityState,
    mergeCanonicalIdentity,
    rekeyCanonicalIdentity,
    resetCanonicalIdentityState,
    splitCanonicalIdentity
  } = useCanonicalIdentityActions({
    frameRecordId: mutableFrameRecordId,
    onFrameReloaded: (record) => {
      updateWorkspaceFrame(record.frame, commitFromFrameRecord(record));
      setDetailPanelOpen(true);
      resetChangeSetContext();
      refreshChangeSets(record.id);
    },
    onInvalidateFrames: invalidateFrameQueries
  });

  const {
    frameIoState,
    frameSharePending,
    dismissFrameIoStatus,
    handleFrameShareClick,
  } = useGraphFrameIo({
    copy: t.frameIo,
    frameRecordId: mutableFrameRecordId,
    onImportedFrame: (nextFrame, commit) => {
      updateWorkspaceFrame(nextFrame, commit);
      setDetailPanelOpen(true);
      invalidateFrameQueries();
      void navigate({ search: (current) => ({ ...current, frame: commit.id }), replace: true });
    },
    onResetChangeSetState: resetChangeSetContext,
    sessionId: threadId
  });

  const reviewFrameMutation = useMutation<GraphFrameCommitResponse, Error, ReviewFrameActionVariables>({
    mutationFn: ({ action, frame, nodeLabel }) =>
      commitGraphFrame({
        sessionId: threadId,
        message: stableReviewCommitMessage(action, nodeLabel),
        frame,
        submittedAt: new Date().toISOString()
      }),
    onSuccess: (commit, variables) => {
      updateWorkspaceFrame(variables.frame, commit);
      setDetailPanelOpen(true);
      invalidateFrameQueries();
      void navigate({ search: (current) => ({ ...current, frame: commit.id }), replace: true });
      resetChangeSetContext();
      resetChangeSetLifecycle();
    },
    onError: (_error, variables) => {
      updateWorkspaceFrame(variables.previous.frame, variables.previous.commit);
      setDetailPanelOpen(true);
    }
  });
  const opportunityActionMutation = useMutation({
    mutationFn: (variables: OpportunityActionVariables) =>
      updateGraphOpportunity(variables.id, { action: variables.action }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["graph-opportunities"] });
    }
  });

  const updateActiveThreadId = (nextThreadId: string) => {
    setThreadId(nextThreadId);
    writeStoredActiveThreadId(nextThreadId);
  };

  const handleNewThread = () => {
    updateActiveThreadId(chatMessageId("thread"));
    setChatMessages(initialAgentMessages);
    setActiveThreadRestoreErrorId(null);
    setThreadSaveError(null);
    setAgentOpen(true);
  };

  const selectThreadMutation = useMutation({
    mutationFn: (nextThreadId: string) => readAgentChatThread(nextThreadId),
    onSuccess: (response, nextThreadId) => {
      setChatMessages(hydrateAgentChatMessages(response.thread.messages));
      updateActiveThreadId(nextThreadId);
      setActiveThreadRestoreErrorId(null);
      setThreadSaveError(null);
      setAgentOpen(true);
    },
    onError: (error, nextThreadId) => {
      // Surfaced (not swallowed) — same reasoning as the mount-resume effect above.
      console.warn(`Failed to load agent chat thread ${nextThreadId}`, error);
      setActiveThreadRestoreErrorId(nextThreadId);
    }
  });

  const handleSelectThread = (nextThreadId: string) => {
    if (nextThreadId === threadId || selectThreadMutation.isPending) {
      return;
    }

    selectThreadMutation.mutate(nextThreadId);
  };

  const deleteThreadMutation = useMutation({
    mutationFn: (targetThreadId: string) => deleteAgentChatThread(targetThreadId),
    onSuccess: (_response, targetThreadId) => {
      invalidateThreadQueries();
      if (targetThreadId === threadId) {
        handleNewThread();
      }
    }
  });

  const handleDeleteThread = (targetThreadId: string) => {
    if (deleteThreadMutation.isPending) {
      return;
    }

    if (!window.confirm(t.workspace.deleteConversationConfirm)) {
      return;
    }

    deleteThreadMutation.mutate(targetThreadId);
  };

  // Non-blocking persistence of the finished transcript: a save failure must not
  // erase the answer already returned into chatMessages, but it is surfaced in the
  // composer so "history did not save" is not mistaken for success.
  const persistAgentChatThreadTranscript = (messages: AgentChatMessage[]) => {
    const payloadMessages = agentChatThreadUpsertMessages(messages);
    if (payloadMessages.length === 0) {
      return;
    }

    setThreadSaveError(null);
    saveAgentChatThread(threadId, { messages: payloadMessages })
      .then(() => {
        invalidateThreadQueries();
        setThreadSaveError(null);
      })
      .catch((error) => {
        console.warn(`Failed to save agent chat thread ${threadId}`, error);
        const detail = error instanceof Error && error.message.trim() ? error.message : "unknown error";
        setThreadSaveError(`Conversation was not saved. ${detail}`);
      });
  };

  const updateAssistantMessage = (assistantMessageId: string, patch: Partial<AgentChatMessage>) => {
    setChatMessages((current) =>
      current.map((chatMessage) =>
        chatMessage.id === assistantMessageId
          ? {
              ...chatMessage,
              ...patch
            }
          : chatMessage
      )
    );
  };

  const mutation = useMutation<AgentChatResult, Error, AgentMutationVariables>({
    mutationFn: async ({ assistantMessageId, chatInput, message: nextMessage }) => {
      let doneResponse: AgentChatResponse | undefined;
      let assistantContent = "";
      const warnings: string[] = [];
      const { streamAgentChatFromEndpoint } = await import("../lib/agentChatStream");
      const providerHeaders = await agentProviderAuthHeaders();
      const showExecutionStatus = (content: string) => {
        if (assistantContent.trim().length === 0) {
          updateAssistantMessage(assistantMessageId, { content });
        }
      };

      try {
        await streamAgentChatFromEndpoint(agentChatStreamEndpoint(), chatInput, {
          ...(providerHeaders ? { headers: providerHeaders } : {}),
          onDone: (response) => {
            doneResponse = response;
            if (response.assistantMessage.content.trim().length > 0) {
              assistantContent = response.assistantMessage.content;
              updateAssistantMessage(assistantMessageId, {
                content: assistantContent
              });
            }
          },
          onError: (error) => {
            warnings.push(error.message);
          },
          onStatus: (status) => {
            if (assistantContent.length === 0) {
              updateAssistantMessage(assistantMessageId, {
                content: statusTextForAgentStream(status, t)
              });
            }
          },
          onTextDelta: (text) => {
            assistantContent += text;
            updateAssistantMessage(assistantMessageId, {
              content: assistantContent
            });
          }
        });
      } catch (error) {
        warnings.push(error instanceof Error ? error.message : t.agentChat.previewStreamFailed);
      }

      if (chatInput.mode === "seeker") {
        // Read-only discussion turn: no frame extraction, no note intake, no commit —
        // a frame is the point of graph-copilot mode, not of seeker mode. Pulled out as
        // a pure function (resolveSeekerTurnResult) so the done/warnings failure matrix
        // — including "partial text arrived, then the stream failed" — is unit-tested
        // directly instead of only through this mutation.
        return resolveSeekerTurnResult({
          assistantContent,
          doneResponse,
          streamFailedMessage: t.agentChat.previewStreamFailed,
          warnings
        });
      }

      if (!doneResponse?.frame || doneResponse.source !== "tanstack-ai-generate") {
        throw new Error(
          noteIntakeExtractionFailureMessage(warnings, {
            base: t.agentChat.noteIntakeExtractionFailure,
            reason: t.agentChat.noteIntakeExtractionFailureReason
          })
        );
      }

      updateAssistantMessage(assistantMessageId, {
        content: assistantContent.trim().length > 0 ? assistantContent : t.agentChat.noteIntakeCapturing
      });
      const intake = await createNoteIntake({
        threadId,
        sessionId: threadId,
        ...(chatInput.currentFrame ? { currentFrame: chatInput.currentFrame } : {}),
        ...(chatInput.currentFrameId ? { currentFrameId: chatInput.currentFrameId } : {}),
        sourceChannel: "web_note",
        languageHint: "auto",
        processingMode: "sync",
        text: nextMessage,
        capturedAt: new Date().toISOString(),
        extractedFrame: doneResponse.frame
      });
      const { commit, frame } = await resolveNoteIntakeCommit(intake, showExecutionStatus);

      const fallbackContent = assistantContentFor({
        assistantContent: "",
        mode: "graph-copilot" as const,
        commit,
        frame,
        warnings
      });
      const result = {
        assistantContent: assistantContent.trim().length > 0 ? assistantContent : fallbackContent,
        mode: "graph-copilot" as const,
        frame,
        commit,
        warnings: [...warnings, ...(doneResponse?.warnings ?? [])]
      };

      return doneResponse?.source ? { ...result, source: doneResponse.source } : result;
    },
    onMutate: ({ assistantMessageId, message: nextMessage, userMessageId }) => {
      setAgentOpen(true);
      setChatMessages((current) => [
        ...current,
        {
          id: userMessageId,
          role: "user",
          status: "done",
          content: nextMessage
        },
        {
          id: assistantMessageId,
          role: "assistant",
          status: "pending",
          content: "Preparing note intake and agent preview."
        }
      ]);
    },
    onSuccess: (result, variables) => {
      // Seeker results carry no frame/commit (read-only discussion) — none of the
      // graph-mutation side effects below apply to that mode.
      if (result.mode === "graph-copilot") {
        updateWorkspaceFrame(result.frame, result.commit);
        setDetailPanelOpen(true);
        invalidateFrameQueries();
        void navigate({ search: (current) => ({ ...current, frame: result.commit.id }), replace: true });
        resetChangeSetLifecycle();
      }
      setAgentOpen(true);
      setMessage("");
      setMentionQuery(null);
      // Flag "a turn just completed" for the chatMessages effect below to pick up —
      // do not try to read the mapped result back out of this updater (see the ref's
      // comment: updater-invocation timing is not guaranteed synchronous).
      pendingThreadSaveRef.current = true;
      setChatMessages((current) =>
        current.map((chatMessage) =>
          chatMessage.id === variables.assistantMessageId
            ? {
                ...chatMessage,
                status: "done",
                content: assistantContentFor(result),
                result
              }
            : chatMessage
        )
      );
      window.requestAnimationFrame(() => textareaRef.current?.focus());
    },
    onError: (error, variables) => {
      setAgentOpen(true);
      const fallbackErrorContent =
        variables.chatInput.mode === "seeker"
          ? "Seeker discussion failed before a reply came back."
          : "The agent route failed before a graph frame came back.";
      setChatMessages((current) =>
        current.map((chatMessage) =>
          chatMessage.id === variables.assistantMessageId
            ? {
                ...chatMessage,
                status: "error",
                content: error.message || fallbackErrorContent
              }
            : chatMessage
        )
      );
    }
  });

  const mentionCandidates = useMemo(() => {
    if (!mentionQuery) {
      return [];
    }

    const term = mentionQuery.text.trim().toLowerCase();
    return frame.graph.nodes
      .map((node) => ({
        node,
        score: mentionCandidateScore(node, term)
      }))
      .filter(({ score }) => score > 0)
      .sort((left, right) => right.score - left.score || left.node.label.localeCompare(right.node.label))
      .map(({ node }) => node)
      .slice(0, 6);
  }, [frame, mentionQuery]);
  const activeMention = mentionCandidates[Math.min(activeMentionIndex, mentionCandidates.length - 1)] ?? null;
  const timelineReplayChangeSet = useMemo(
    () => timelineReplayChangeSetWrite(frame, timeline, timelineFocus, zoomLevel),
    [frame, timeline, timelineFocus, zoomLevel]
  );

  useEffect(() => {
    if (!selectedNodeId || relationshipPathTargetNodes.length === 0) {
      setRelationshipPathTargetId("");
      return;
    }

    setRelationshipPathTargetId((current) =>
      current.length > 0 && relationshipPathTargetNodes.some((candidate) => candidate.id === current)
        ? current
        : relationshipPathTargetNodes[0]?.id ?? ""
    );
  }, [relationshipPathTargetNodes, selectedNodeId]);

  useEffect(() => {
    const signinProvider = search.signin;
    if (!signinProvider) {
      return;
    }

    if (isSharedReadOnly) {
      const share = search.share;
      if (share) {
        void navigate({ search: { share }, replace: true });
      }
      return;
    }

    if (hasStoredSession) {
      void navigate({
        search: (current) => {
          const { signin: _signin, ...withoutSignin } = current;
          return withoutSignin;
        },
        replace: true
      });
      return;
    }

    if (availableAuthProviders.includes(signinProvider)) {
      handleAuthProviderSignIn(signinProvider);
    }
  }, [
    availableAuthProviders,
    handleAuthProviderSignIn,
    hasStoredSession,
    isSharedReadOnly,
    navigate,
    search.frame,
    search.share,
    search.signin
  ]);

  useEffect(() => {
    if (!latestFrameRestoreId) {
      return;
    }

    void navigate({ search: (current) => ({ ...current, frame: latestFrameRestoreId }), replace: true });
  }, [latestFrameRestoreId, navigate]);

  useEffect(() => {
    // An explicit `?view=` is the navigation authority. Only project the
    // frame's persisted graph view when the URL has not selected a view yet;
    // otherwise a header click could be overwritten on the next effect pass.
    if (search.view) {
      return;
    }

    const nextView = workspaceViewAfterFrameUpdate(activeView, frame.ui.activeView);
    if (nextView !== activeView && isWorkspaceViewId(nextView)) {
      setActiveView(nextView);
    }
  }, [activeView, frame.frameId, frame.ui.activeView, lastResult?.commit.id, search.view, setActiveView]);

  useEffect(() => {
    if (firstRunOnboardingChecked) {
      return;
    }
    setFirstRunOnboardingChecked(true);

    if (
      !shouldOpenFirstRunOnboarding({
        explicitView: search.view,
        isSharedReadOnly,
        onboardingAvailable: shouldShowFirstRunOnboarding()
      })
    ) {
      return;
    }

    setFirstRunOnboardingActive(true);
    setActiveView("help");
    setDetailPanelOpen(true);
  }, [firstRunOnboardingChecked, isSharedReadOnly, search.view]);

  // Resume the thread persisted in localStorage (see threadId's initializer) once
  // sign-in state is known. A stale id is cleared only after the authoritative
  // read endpoint returns not_found.
  useEffect(() => {
    if (activeThreadRestoreAttempted || isSharedReadOnly || !hasStoredSession) {
      return;
    }

    setActiveThreadRestoreAttempted(true);
    const storedThreadId = readStoredActiveThreadId();
    if (!storedThreadId) {
      return;
    }

    readAgentChatThread(storedThreadId)
      .then((response) => {
        setChatMessages(hydrateAgentChatMessages(response.thread.messages));
        setActiveThreadRestoreErrorId(null);
      })
      .catch((error) => {
        if (isAgentChatThreadNotFoundError(error)) {
          updateActiveThreadId(chatMessageId("thread"));
          setChatMessages(initialAgentMessages);
          setActiveThreadRestoreErrorId(null);
          setThreadSaveError(null);
          return;
        }

        // Surfaced (not swallowed): a 401/403/503/ownership conflict must not look
        // identical to "no history yet" — the left rail renders this id as an error.
        console.warn(`Failed to resume agent chat thread ${storedThreadId}`, error);
        setActiveThreadRestoreErrorId(storedThreadId);
      });
  }, [activeThreadRestoreAttempted, hasStoredSession, isSharedReadOnly]);

  // Restore a committed frame whenever ?frame= points somewhere else than the loaded
  // one (deep link, reload, or a click in the Recent frames rail).
  useEffect(() => {
    const record = frameRestoreQuery.data;
    if (!record || record.id !== search.frame || record.id === lastResult?.commit.id) {
      return;
    }

    updateWorkspaceFrame(record.frame, commitFromFrameRecord(record));
    // Every cross-frame jump lands here, so this is the one place the jump state is
    // resolved: consume the pending jump and re-adjudicate the failure notice.
    const step = searchSelectionTransition(searchSelection, {
      kind: "frame-restored",
      frameRecordId: record.id,
      nodeIds: record.frame.graph.nodes.map((node) => node.id)
    });
    setSearchSelection(step.state);
    if (step.selectNodeId) {
      handleSelectNode(step.selectNodeId);
      setSearchQuery("");
    }
    setDetailPanelOpen(true);
    resetChangeSetContext();
    resetCanonicalIdentityState();
  }, [frameRestoreQuery.data, search.frame, lastResult?.commit.id, searchSelection]);

  useEffect(() => {
    const response = sharedFrameQuery.data;
    const record = response?.record;
    if (!record || record.id === lastResult?.commit.id) {
      return;
    }

    updateWorkspaceFrame(record.frame, commitFromFrameRecord(record));
    setDetailPanelOpen(true);
    resetChangeSetContext();
    resetCanonicalIdentityState();
  }, [sharedFrameQuery.data, lastResult?.commit.id]);

  const handleSelectFrameRecord = (frameRecordId: string) => {
    if (frameRecordId === lastResult?.commit.id) {
      return;
    }

    void navigate({ search: (current) => ({ ...current, frame: frameRecordId }) });
  };

  const handleAcceptReview = (nodeId: string) => {
    if (!lastResult) {
      return;
    }

    const nodeLabel = frame.graph.nodes.find((node) => node.id === nodeId)?.label ?? "candidate";
    const result = acceptSelectedReview(nodeId);
    if (!result) {
      return;
    }

    reviewFrameMutation.mutate({ action: "accepted", frame: result.frame, nodeLabel, previous: lastResult });
  };

  const handleDiscardReview = (nodeId: string) => {
    if (!lastResult) {
      return;
    }

    const nodeLabel = frame.graph.nodes.find((node) => node.id === nodeId)?.label ?? "candidate";
    const result = discardSelectedReview(nodeId);
    if (!result) {
      return;
    }

    reviewFrameMutation.mutate({ action: "discarded", frame: result.frame, nodeLabel, previous: lastResult });
  };

  const handleCreateNodeEdit = (node: GraphNode, draft: NodeEditDraft) => {
    const input = nodeEditChangeSetWrite(node, draft);
    if (!input) {
      return;
    }

    createPendingChangeSet({
      input,
      sourceLabel: t.changeSet.sources.nodeEdit(node.label)
    });
  };

  // Same create-then-leave-pending flow as every other node mutation here
  // (handleCreateNodeEdit, relationship handlers, timeline replay): apply is a
  // separate explicit step the user takes from the pending change-set banner.
  const handleToggleLeadStage = (node: GraphNode, target: "current" | null) => {
    createPendingChangeSet({
      input: leadStageToggleChangeSetWrite(node, target),
      sourceLabel:
        target === "current"
          ? t.changeSet.sources.leadStageMoveIn(node.label)
          : t.changeSet.sources.leadStageMoveOut(node.label)
    });
  };

  const handleCreateRelationship = (sourceNode: GraphNode, draft: RelationshipCreateDraft) => {
    const input = relationshipCreateChangeSetWrite(frame, sourceNode, draft);
    if (!input) {
      return;
    }

    const targetNode = frame.graph.nodes.find((candidate) => candidate.id === draft.targetNodeId);
    createPendingChangeSet({
      input,
      sourceLabel: t.changeSet.sources.relationship(sourceNode.label, targetNode?.label ?? t.changeSet.sources.targetFallback)
    });
  };

  const handleEditRelationship = (edge: GraphEdge, draft: RelationshipEditDraft) => {
    const input = relationshipEditChangeSetWrite(edge, draft);
    if (!input) {
      return;
    }

    createPendingChangeSet({
      input,
      sourceLabel: t.changeSet.sources.relationshipEdit(edge.label)
    });
  };

  const handleDeleteRelationship = (edge: GraphEdge) => {
    const input = relationshipDeleteChangeSetWrite(edge);
    createPendingChangeSet({
      input,
      sourceLabel: t.changeSet.sources.relationshipDelete(edge.label)
    });
  };

  const handleLogReminderTouchpoint = (edge: GraphEdge) => {
    createPendingChangeSet({
      input: relationshipTouchpointChangeSetWrite(edge, new Date().toISOString()),
      sourceLabel: t.changeSet.sources.relationshipTouchpoint(edge.label)
    });
  };

  const handleOpenReminderFrame = (target: { frameRecordId: string; nodeId: string }) => {
    if (target.frameRecordId === lastResult?.commit.id) {
      handleWorkspaceSelectNode(target.nodeId);
      return;
    }

    applySearchSelectionEvent({ kind: "cross-frame-jump", ...target });
    void navigate({ search: (current) => ({ ...current, frame: target.frameRecordId }) });
  };

  const handleSaveTimelineReplay = () => {
    if (!timelineReplayChangeSet) {
      return;
    }

    const moment = timeline.moments[timelineFocus] ?? timeline.moments[timeline.moments.length - 1];
    createPendingChangeSet({
      input: timelineReplayChangeSet,
      sourceLabel: t.changeSet.sources.timelineReplay(moment?.event.label ?? t.changeSet.sources.currentMoment)
    });
  };

  const handleWorkspaceViewChange = (viewId: WorkspaceViewId) => {
    applySearchSelectionEvent({ kind: "change-view" });
    setActiveView(viewId);
    setSearchQuery("");
    setFilters(workspaceFiltersForView(viewId));
    if (isCanonicalRegistryView(viewId)) {
      setAgentOpen(false);
      setDetailPanelOpen(true);
    }

    switch (viewId) {
      case "overview":
        handleViewModeChange("flat");
        break;
      case "events":
        handleViewModeChange("timeline");
        setDetailTab("events");
        break;
      case "people":
        handleViewModeChange("flat");
        setDetailTab("people");
        break;
      case "companies":
        handleViewModeChange("flat");
        setDetailTab("companies");
        break;
      case "signals":
        handleViewModeChange("flat");
        setDetailTab("opportunities");
        setDetailPanelOpen(true);
        break;
      case "current":
        handleViewModeChange("flat");
        setDetailPanelOpen(true);
        break;
      case "schedule":
        handleViewModeChange("flat");
        setAgentOpen(false);
        setDetailPanelOpen(true);
        break;
      case "privateAgent":
        handleViewModeChange("flat");
        setAgentOpen(false);
        setDetailPanelOpen(true);
        break;
      case "help":
        handleViewModeChange("flat");
        setAgentOpen(false);
        setDetailPanelOpen(true);
        break;
      default:
        handleViewModeChange("flat");
        setDetailTab("companies");
        break;
    }
  };

  const handleOpenSettings = () => {
    void navigate({
      to: "/settings/$section",
      params: { section: "account" },
      search: mutableFrameRecordId ? { frame: mutableFrameRecordId } : {}
    });
  };

  /** Opens one independently addressable Settings subpage. */
  const handleOpenSettingsSection = (section: "computers" | "private-agent") => {
    void navigate({
      to: "/settings/$section",
      params: { section },
      search: mutableFrameRecordId ? { frame: mutableFrameRecordId } : {}
    });
  };

  const handleOpenPrivateAgentJobs = () => {
    handleWorkspaceViewChange("privateAgent");
  };

  // A person or opportunity is the only thing research can target: the job's
  // `targetCanonicalEntityId` is derived from a node's type and label.
  const researchTargetNode =
    selectedNode && (selectedNode.type === "person" || selectedNode.type === "opportunity") ? selectedNode : null;

  const handleStartPrivateAgentResearch = () => {
    if (!researchTargetNode || !mutableFrameRecordId || !privateAgent.profile || !selectedAgentId) {
      return;
    }
    privateAgent.createJob({
      agentId: selectedAgentId,
      // One idempotency key per click: a retry of this mutation must not create
      // a second job. The research preset is the server's read of the tenant's
      // Private Agent profile, so the client sends the schema default.
      clientRequestId: newAgentResearchClientRequestId(),
      frameRecordId: mutableFrameRecordId,
      question: t.privateAgentJobs.question(researchTargetNode.label),
      targetCanonicalEntityId: canonicalEntityIdForGraphNode(researchTargetNode)
    });
  };

  const researchTrigger =
    researchTargetNode && !isSharedReadOnly ? (
      <>
      {/* The Private Agent workspace already picks the agent in its left rail; every
          other view has no other place to choose one. */}
      {!isPrivateAgentWorkspace ? (
        <PrivateAgentSelector agents={privateAgent.agents.map((agent) => agent.profile)} agentId={selectedAgentId} onChange={setPrivateAgentSelection} disabled={!canUsePrivateAgent} />
      ) : null}
      <PrivateAgentResearchTrigger
        agentSelected={Boolean(selectedAgentId)}
        agentName={privateAgentName}
        blockReason={privateAgentResearchBlockReason({
          computerAvailability: privateAgentComputer,
          entitlement: privateAgent.entitlement,
          frameRecordId: mutableFrameRecordId,
          hasSession: canUsePrivateAgent,
          jobs: privateAgent.jobs,
          profile: privateAgent.profile
        })}
        createError={privateAgent.createError}
        createPending={privateAgent.createPending}
        onOpenComputers={() => handleOpenSettingsSection("computers")}
        onOpenJobs={handleOpenPrivateAgentJobs}
        onOpenPlans={() => handleOpenBilling()}
        onOpenPrivateAgentSettings={() => handleOpenSettingsSection("private-agent")}
        onStart={handleStartPrivateAgentResearch}
      />
      </>
    ) : null;

  const handleOpenBilling = () => {
    window.location.assign("/pricing");
  };

  const handleAccountSignOut = () => {
    clearStoredSaleskoSession();
    queryClient.removeQueries({
      predicate: (query) => {
        const root = query.queryKey[0];
        return typeof root === "string" && tenantScopedQueryKeyRoots.has(root);
      }
    });
    handleWorkspaceViewChange("graph");
  };

  const handleOpenCanonicalRegistryFromDetail = (tab: DetailTab) => {
    const viewId = canonicalRegistryViewForDetailTab(tab);
    if (!viewId || !isWorkspaceViewId(viewId)) {
      return;
    }

    handleWorkspaceViewChange(viewId);
  };

  const handleOpenOnboarding = () => {
    setAgentOpen(false);
    setActiveView("help");
    setDetailPanelOpen(true);
  };

  const handleDismissFirstRunOnboarding = () => {
    markFirstRunOnboardingDismissed();
    setFirstRunOnboardingActive(false);
    setActiveView("graph");
    setDetailPanelOpen(true);
  };

  const handleWorkspaceSelectNode = (nodeId: string) => {
    applySearchSelectionEvent({ kind: "select-node" });
    handleSelectNode(nodeId);
    setActiveView(viewAfterNodeSelect(activeView));
    setDetailPanelOpen(true);
  };

  /**
   * Opening one entity the assistant read during this conversation. Where it has
   * to go is decided by `privateAgentTouchedEntitySelection`; this handler owns
   * only the side effects — and unlike `handleOpenLatestFrameNode` it keeps the
   * user in Chat instead of forcing the graph view. The side panel returns to
   * `详情`, which is where the opened entity is shown.
   */
  const handlePrivateAgentTouchedEntitySelect = (entity: PrivateAgentTouchedEntityRow) => {
    setPrivateAgentDetailTab("detail");
    const selection = privateAgentTouchedEntitySelection({
      activeView,
      entry: entity,
      loadedFrameRecordId: lastResult?.commit.id ?? null,
      loadedNodeIdForCanonicalId:
        frame?.graph.nodes.find(
          (candidate) => canonicalEntityIdForGraphNode(candidate) === entity.canonicalEntityId
        )?.id ?? null
    });

    if (selection.kind === "in-frame") {
      handleWorkspaceSelectNode(selection.nodeId);
      return;
    }

    clearSelection();
    applySearchSelectionEvent({
      kind: "cross-frame-jump",
      frameRecordId: selection.frameRecordId,
      nodeId: selection.nodeId
    });
    setDetailPanelOpen(true);
    setActiveView(selection.nextView);
    void navigate({ search: (current) => ({ ...current, frame: selection.frameRecordId }) });
  };

  const handleWorkspaceSelectEdge = (edgeId: string) => {
    applySearchSelectionEvent({ kind: "select-edge" });
    handleSelectEdge(edgeId);
    setActiveView("graph");
    setDetailPanelOpen(true);
  };

  const handleSavedSearchSelect = (node: GraphReadNode) => {
    if (node.frameRecordId === lastResult?.commit.id) {
      handleWorkspaceSelectNode(node.id);
      setSearchQuery("");
      return;
    }

    clearSelection();
    applySearchSelectionEvent({ kind: "cross-frame-jump", frameRecordId: node.frameRecordId, nodeId: node.id });
    setDetailPanelOpen(true);
    setActiveView(viewAfterNodeSelect(activeView));
    setSearchQuery("");
    void navigate({ search: (current) => ({ ...current, frame: node.frameRecordId }) });
  };

  const handleOpenLatestFrameNode = (target: { frameRecordId: string; nodeId: string }) => {
    setSearchQuery("");
    setAgentOpen(false);
    setDetailPanelOpen(true);
    setActiveView("graph");

    if (target.frameRecordId === lastResult?.commit.id) {
      handleWorkspaceSelectNode(target.nodeId);
      return;
    }

    applySearchSelectionEvent({ kind: "cross-frame-jump", ...target });

    void navigate({ search: (current) => ({ ...current, frame: target.frameRecordId }) });
  };

  const handleCanonicalEntitySelect = (entity: GraphCanonicalEntity) => {
    handleOpenLatestFrameNode({ frameRecordId: entity.latestFrameRecordId, nodeId: entity.latestNodeId });
  };

  const handleOverviewNodeSelect = (nodeId: string) => {
    const overviewNode = overviewFrame?.graph.nodes.find((candidate) => candidate.id === nodeId);
    const latestFrameRecordId = stringDataValue(overviewNode?.data.latestFrameRecordId);
    const latestNodeId = stringDataValue(overviewNode?.data.latestNodeId);
    if (latestFrameRecordId && latestNodeId) {
      handleOpenLatestFrameNode({ frameRecordId: latestFrameRecordId, nodeId: latestNodeId });
      return;
    }

    const entityId = overviewEntityIdFromNodeId(nodeId);
    const entity = entityId ? canonicalRegistryQuery.data?.items.find((candidate) => candidate.id === entityId) : null;
    if (entity) {
      handleCanonicalEntitySelect(entity);
      return;
    }

    handleWorkspaceSelectNode(nodeId);
  };

  const handleOpportunityAction = (id: string, action: GraphOpportunityActionRequest["action"]) => {
    opportunityActionMutation.mutate({ id, action });
  };

  const handleOpenSignalFrame = (frameRecordId: string) => {
    setSearchQuery("");
    setAgentOpen(false);
    setDetailPanelOpen(true);
    setActiveView("signals");
    void navigate({ search: (current) => ({ ...current, frame: frameRecordId }) });
  };

  const handleSelectActivitySource = (entryId: string, sourceReferenceHash: string) => {
    setSelectedTransactionId(null);
    setSelectedActivitySource({ entryId, sourceReferenceHash });
  };

  const handleSelectTransaction = (canonicalTransactionId: string) => {
    setSelectedActivitySource(null);
    setSelectedTransactionId(canonicalTransactionId);
  };

  const handleOpenTransactionGraph = (canonicalTransactionId: string) => {
    setSelectedActivitySource(null);
    setSelectedTransactionId(null);
    setSelectedTransactionGraphId(canonicalTransactionId);
  };

  const handleCloseTransactionGraph = () => {
    const closingId = selectedTransactionGraphId;
    setSelectedTransactionGraphId(null);
    if (!closingId || typeof requestAnimationFrame !== "function") return;
    requestAnimationFrame(() => {
      const trigger = Array.from(
        document.querySelectorAll<HTMLButtonElement>("[data-transaction-graph-id]")
      ).find((button) => button.dataset.transactionGraphId === closingId);
      trigger?.focus();
    });
  };

  const handleOpenActivitySourceFrame = (frameRecordId: string, nodeId?: string) => {
    const resolvedSource = activitySourceDetailQuery.data;
    if (resolvedSource?.frameRecord.id === frameRecordId) {
      queryClient.setQueryData(["graph-frame", frameRecordId], resolvedSource.frameRecord);
    }
    setSearchQuery("");
    setAgentOpen(false);
    setDetailPanelOpen(true);
    setActiveView("graph");
    if (nodeId) {
      handleOpenLatestFrameNode({ frameRecordId, nodeId });
      return;
    }
    void navigate({ search: (current) => ({ ...current, frame: frameRecordId }) });
  };

  const handleWorkspaceRecenter = () => {
    handleRecenter();
    setDetailPanelOpen(true);
  };

  const handleGraphSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // Same precedence the rendered result list uses for clicks: the current
      // frame's first hit wins, and only an empty current list falls back to the
      // saved (cross-frame) hit.
      const currentHit = searchResults[0];
      const savedHit = savedSearchResults[0];
      if (currentHit) {
        handleWorkspaceSelectNode(currentHit.id);
        setSearchQuery("");
      } else if (savedHit) {
        handleSavedSearchSelect(savedHit);
      }
    }
    if (event.key === "Escape") {
      if (searchQuery.trim().length > 0) event.stopPropagation();
      setSearchQuery("");
    }
  };

  useEffect(() => {
    setActiveMentionIndex(0);
  }, [frame.frameId, mentionQuery?.text]);

  useEffect(() => {
    if (!agentOpen) {
      return;
    }
    const thread = agentThreadRef.current;
    if (!thread) {
      return;
    }
    thread.scrollTop = thread.scrollHeight;
  }, [agentOpen, chatMessages]);

  // Runs after every chatMessages commit; only actually saves when onSuccess just set
  // the ref (below, it's a cheap no-op). By the time an effect body runs, React has
  // already committed the update, so `chatMessages` here is guaranteed to include the
  // "done" flip — unlike reading it back out of the setChatMessages updater itself.
  useEffect(() => {
    if (!pendingThreadSaveRef.current) {
      return;
    }

    pendingThreadSaveRef.current = false;
    persistAgentChatThreadTranscript(chatMessages);
  }, [chatMessages]);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || document.querySelector("dialog:modal")) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setAgentOpen(false);
        window.requestAnimationFrame(() => {
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        });
        return;
      }

      if (event.key !== "Escape") {
        return;
      }

      if (agentOpen) {
        event.preventDefault();
        setAgentOpen(false);
        return;
      }

      if (detailPanelOpen) {
        event.preventDefault();
        setDetailPanelOpen(false);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [agentOpen, detailPanelOpen]);

  const mutationError = mutation.error instanceof Error ? mutation.error.message : null;

  const updateMentionFromTextarea = (value: string, cursor: number) => {
    setMentionQuery(findActiveMention(value, cursor));
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextMessage = event.currentTarget.value;
    setMessage(nextMessage);
    updateMentionFromTextarea(nextMessage, event.currentTarget.selectionStart ?? nextMessage.length);
  };

  // The empty-state example is a one-tap suggestion, not a permanent block: tapping it
  // fills the composer with the sample so the user can edit or send it straight away.
  const handleUseSample = () => {
    setMessage(sampleText);
    setMentionQuery(null);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(sampleText.length, sampleText.length);
    });
  };

  const handleMessageCursorChange = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = event.currentTarget;
    updateMentionFromTextarea(target.value, target.selectionStart ?? target.value.length);
  };

  const handleMessageKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      suppressMentionEscapeKeyUpRef.current = false;
      return;
    }

    handleMessageCursorChange(event);
  };

  const insertMention = (node: GraphNode) => {
    if (!mentionQuery) {
      return;
    }

    const token = mentionTokenFor(node);
    const before = message.slice(0, mentionQuery.start);
    const after = message.slice(mentionQuery.end);
    // Always append a trailing space after an inserted @mention (unless the
    // following text already starts with whitespace) so the mention token keeps
    // a boundary. Without it, a mention completed at the end of the message runs
    // straight into the next keystrokes and no longer re-resolves to its node.
    const spacer = /^\s/.test(after) ? "" : " ";
    const nextMessage = `${before}${token}${spacer}${after}`;
    const nextCursor = before.length + token.length + spacer.length;
    setMessage(nextMessage);
    setMentionQuery(null);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const submitAgentMessage = () => {
    const nextMessage = message.trim();
    if (nextMessage.length === 0 || mutation.isPending) {
      return;
    }

    const userMessageId = chatMessageId("user");
    mutation.mutate({
      chatInput: createAgentChatInput({
        activeNodeId: selectedNodeId,
        frame,
        persistedFrame: lastResult?.frame ?? null,
        history: chatMessages,
        message: nextMessage,
        mode: agentMode,
        threadId,
        userMessageId
      }),
      message: nextMessage,
      userMessageId,
      assistantMessageId: chatMessageId("assistant")
    });
  };

  const handleMessageKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery && event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      suppressMentionEscapeKeyUpRef.current = true;
      setMentionQuery(null);
      return;
    }

    if (mentionQuery && mentionCandidates.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveMentionIndex((current) => (current + 1) % mentionCandidates.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveMentionIndex((current) => (current - 1 + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if ((event.key === "Enter" || event.key === "Tab") && activeMention) {
        event.preventDefault();
        insertMention(activeMention);
        return;
      }
    }

    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      submitAgentMessage();
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitAgentMessage();
  };

  const activityPanel: ActivityFeedPanelProps | null = transactionActivityEnabled && hasStoredSession
    ? {
        enabled: canReadActivityFeed,
        hasNext: activityFeedQuery.hasNextPage === true,
        initialError:
          activityFeedProjection.error && activityFeedItems.length === 0
            ? t.activityFeed.unavailable
            : activityFeedQuery.isError && !activityFeedQuery.data && activityFeedQuery.error instanceof Error
              ? activityFeedQuery.error.message
              : undefined,
        items: activityFeedItems,
        loadingMore: activityFeedQuery.isFetchingNextPage,
        loadMoreError:
          activityFeedProjection.error && activityFeedItems.length > 0
            ? t.activityFeed.unavailable
            : activityFeedQuery.isFetchNextPageError && activityFeedQuery.error instanceof Error
              ? activityFeedQuery.error.message
              : undefined,
        onCloseSourceDetail: () => setSelectedActivitySource(null),
        onCloseTransactionDetail: () => setSelectedTransactionId(null),
        onLoadMore: () => {
          void activityFeedQuery.fetchNextPage();
        },
        onOpenSourceFrame: handleOpenActivitySourceFrame,
        onOpenTransactionGraph: handleOpenTransactionGraph,
        onSelectSource: handleSelectActivitySource,
        onSelectTransaction: handleSelectTransaction,
        pending: canReadActivityFeed && activityFeedQuery.isPending,
        selectedSource: selectedActivitySource,
        selectedTransactionId,
        sourceDetail: activitySourceDetailQuery.data ?? null,
        sourceDetailError:
          activitySourceDetailQuery.error instanceof Error ? activitySourceDetailQuery.error.message : undefined,
        sourceDetailPending: selectedActivitySource !== null && activitySourceDetailQuery.isPending,
        transactionDetail: transactionDetailQuery.data ?? null,
        transactionDetailError:
          transactionDetailQuery.error instanceof Error ? transactionDetailQuery.error.message : undefined,
        transactionDetailPending: selectedTransactionId !== null && transactionDetailQuery.isPending
      }
    : null;

  const transactionGraphDialog = (
    <TransactionGraphDialog
      error={transactionGraphQuery.error instanceof Error ? transactionGraphQuery.error.message : undefined}
      onClose={handleCloseTransactionGraph}
      pending={selectedTransactionGraphId !== null && transactionGraphQuery.isPending}
      response={transactionGraphQuery.data ?? null}
      transactionId={selectedTransactionGraphId}
    />
  );
  const searchSelectionStatus = searchSelectionNotice({
    pending: searchSelection.pending,
    restoreFailed: frameRestoreQuery.isError,
    unavailableNodeId: searchSelection.unavailable
  });

  // Below the design system's --bp-md (768px) the three-pane desktop grid is replaced by
  // the PWA app shell: top bar + scrollable content + bottom tab bar + 2D flat graph.
  // The shell reuses the same frame/filters/selection state and agent handlers — only the
  // presentation forks here, so there is no parallel data/logic implementation.
  if (isMobile && !isPrivateAgentWorkspace) {
    return (
      <>
      <MobileWorkspace
        frame={frame}
        filters={filters}
        onToggleFilter={(entityType) => {
          setActiveView("graph");
          setFilters((current) => ({
            ...current,
            [entityType]: !current[entityType]
          }));
        }}
        selectedNode={selectedNode}
        onSelectNode={handleWorkspaceSelectNode}
        onCloseDetail={() => setDetailPanelOpen(false)}
        isSelfNode={Boolean(selectedNode && selfNodeId === selectedNode.id)}
        onSetSelfNode={() => {
          if (selectedNode) {
            setSelfNode(selectedNode);
          }
        }}
        onClearSelfNode={clearSelfNode}
        message={message}
        agentMode={agentMode}
        onAgentModeChange={setAgentMode}
        onMessageChange={handleMessageChange}
        onSubmitAgent={submitAgentMessage}
        sampleText={sampleText}
        onUseSample={handleUseSample}
        chatMessages={chatMessages}
        agentBusy={mutation.isPending}
        signals={{
          activity: activityPanel,
          actionError: opportunityActionMutation.error instanceof Error ? opportunityActionMutation.error.message : undefined,
          actionPendingId: opportunityActionMutation.isPending ? (opportunityActionMutation.variables?.id ?? null) : null,
          count: opportunityFeedQuery.data?.count ?? 0,
          enabled: canReadOpportunitySignals,
          error: opportunityFeedQuery.error instanceof Error ? opportunityFeedQuery.error.message : undefined,
          items: opportunityFeedQuery.data?.items ?? [],
          onAction: handleOpportunityAction,
          onOpenFrame: handleOpenSignalFrame,
          pending: canReadOpportunitySignals && opportunityFeedQuery.isPending,
          readOnly: isSharedReadOnly
        }}
        inbox={{
          activeFrameRecordId: lastResult?.commit.id ?? null,
          error: isSharedReadOnly ? false : frameListQuery.isError,
          items: isSharedReadOnly ? [] : (frameListQuery.data?.items ?? []),
          onSelectFrameRecord: handleSelectFrameRecord,
          pending: isSharedReadOnly ? false : frameListQuery.isPending,
          restoreErrorFrameId:
            sharedFrameQuery.isError && search.share
              ? "shared link"
              : frameRestoreQuery.isError && search.frame
                ? search.frame
                : null
        }}
        profile={{
          agentAuthLabel,
          availableAuthProviders,
          install,
          onOpenBilling: handleOpenBilling,
          onOpenSettings: handleOpenSettings,
          onSignIn: handleAuthProviderSignIn,
          onSignOut: handleAccountSignOut,
          storedSession
        }}
        notifications={{
          error:
            relationshipRemindersQuery.error instanceof Error
              ? relationshipRemindersQuery.error.message
              : opportunityFeedQuery.error instanceof Error
                ? opportunityFeedQuery.error.message
                : undefined,
          onOpenReminderFrame: handleOpenReminderFrame,
          onOpenSignalFrame: handleOpenSignalFrame,
          opportunitySignals: opportunityFeedQuery.data?.items ?? [],
          pending:
            (canReadRelationshipReminders && relationshipRemindersQuery.isPending) ||
            (canReadOpportunitySignals && opportunityFeedQuery.isPending),
          relationshipReminders: relationshipRemindersQuery.data?.items ?? []
        }}
      />
      {transactionGraphDialog}
      </>
    );
  }

  // The detail panel is a modal drawer only in the narrow Private Agent layout.
  const detailIsDrawer = isPrivateAgentWorkspace && detailDrawer;

  // The entity detail body is the fallback for two surfaces: the `tenant` view
  // relabels the panel but keeps this content, and every view with no surface of
  // its own lands here.
  const renderEntityDetail = () => (
    <DetailPanel
      changeSetAuditExport={changeSetAuditExportState}
      changeSetAuditLog={changeSetAuditLogState}
      changeSetHistory={changeSetHistoryState}
      canonicalIdentityPending={canonicalIdentityPending}
      canonicalIdentityState={canonicalIdentityState}
      canOpenCanonicalRegistry={!isSharedReadOnly && hasStoredSession}
      commit={isSharedReadOnly ? null : lastResult?.commit ?? null}
      detailTab={detailTab}
      frame={frame}
      hasSession={hasStoredSession}
      isSelfNode={Boolean(selectedNode && selfNodeId === selectedNode.id)}
      linkedNodes={visibleLinkedNodes}
      node={selectedNode}
      onClearSelfNode={clearSelfNode}
      onRequestSearch={() => {
        if (isPrivateAgentWorkspace && detailDrawer) setDetailPanelOpen(false);
        window.requestAnimationFrame(() => searchInputRef.current?.focus());
      }}
      onSetSelfNode={() => {
        if (selectedNode) {
          setSelfNode(selectedNode);
        }
      }}
      selectedEdgeId={selectedEdgeId}
      changeSetCreatePending={createChangeSetPending}
      onAcceptReview={handleAcceptReview}
      onApplyChangeSet={applyChangeSet}
      onCreateNodeEdit={handleCreateNodeEdit}
      onCreateRelationship={handleCreateRelationship}
      onDeleteRelationship={handleDeleteRelationship}
      onEditRelationship={handleEditRelationship}
      onDiscardReview={handleDiscardReview}
      onInspectChangeSetAudit={inspectChangeSetAudit}
      onExportChangeSetAudit={exportChangeSetAudit}
      onLogReminderTouchpoint={handleLogReminderTouchpoint}
      onMergeCanonicalIdentity={mergeCanonicalIdentity}
      onOpenCanonicalRegistry={handleOpenCanonicalRegistryFromDetail}
      onOpenReminderFrame={handleOpenReminderFrame}
      onRefreshChangeSets={refreshChangeSets}
      onRekeyCanonicalIdentity={rekeyCanonicalIdentity}
      onSelectNode={handleWorkspaceSelectNode}
      onSplitCanonicalIdentity={splitCanonicalIdentity}
      onTabChange={setDetailTab}
      onToggleLeadStage={handleToggleLeadStage}
      privateAgentName={privateAgentName}
      readOnly={isSharedReadOnly}
      relationshipIntelligence={relationshipIntelligence}
      researchTrigger={researchTrigger}
      reviewActionPending={reviewFrameMutation.isPending}
      onRelationshipPathTargetChange={setRelationshipPathTargetId}
      tabs={detailTabs}
    />
  );

  // One record per detail surface instead of three parallel `activeView` chains
  // that had to be kept in sync by eye. `closeTitle` differs from `closeLabel`
  // on purpose — the tooltip is shorter than the accessible name. Explicit views
  // are matched before `canonicalRegistryVisible` (overview/people/companies/
  // events/signals), which is the widest condition here.
  const detailSurface = ((): { label: string; closeLabel: string; closeTitle: string; content: ReactNode } => {
    if (activeView === "help") {
      return {
        label: t.onboarding.eyebrowGuide,
        closeLabel: "Close workspace guide",
        closeTitle: "Close workspace guide",
        content: (
          <OnboardingPanel
            availableAuthProviders={availableAuthProviders}
            frame={frame}
            hasCommittedFrame={Boolean(mutableFrameRecordId)}
            hasStoredSession={hasStoredSession}
            isFirstRun={firstRunOnboardingActive}
            onDismissFirstRun={handleDismissFirstRunOnboarding}
            onSignIn={handleAuthProviderSignIn}
            onOpenAgent={() => {
              setAgentOpen(true);
            }}
            onOpenSettings={handleOpenSettings}
            readOnly={isSharedReadOnly}
          />
        )
      };
    }
    if (activeView === "tenant" && !isSharedReadOnly) {
      return {
        label: t.workspace.tenantControlsAriaLabel,
        closeLabel: "Close tenant controls",
        closeTitle: "Close tenant controls",
        content: renderEntityDetail()
      };
    }
    if (activeView === "signals") {
      return {
        label: t.activityFeed.surfaceAriaLabel,
        closeLabel: t.activityFeed.closeSurface,
        closeTitle: t.activityFeed.closeSurface,
        content: (
          <SignalsPanel
            activity={activityPanel}
            actionError={
              opportunityActionMutation.error instanceof Error ? opportunityActionMutation.error.message : undefined
            }
            actionPendingId={opportunityActionMutation.isPending ? (opportunityActionMutation.variables?.id ?? null) : null}
            count={opportunityFeedQuery.data?.count ?? 0}
            enabled={canReadOpportunitySignals}
            error={opportunityFeedQuery.error instanceof Error ? opportunityFeedQuery.error.message : undefined}
            items={opportunityFeedQuery.data?.items ?? []}
            onAction={handleOpportunityAction}
            onOpenFrame={handleOpenSignalFrame}
            pending={canReadOpportunitySignals && opportunityFeedQuery.isPending}
            readOnly={isSharedReadOnly}
          />
        )
      };
    }
    if (activeView === "schedule") {
      return {
        label: t.schedule.title,
        closeLabel: t.schedule.close,
        closeTitle: t.schedule.close,
        content: (
          <SchedulePanel
            data={scheduleQuery.data}
            enabled={canReadSchedule}
            error={scheduleQuery.error instanceof Error ? scheduleQuery.error.message : undefined}
            pending={canReadSchedule && scheduleQuery.isPending}
          />
        )
      };
    }
    if (activeView === "current") {
      return {
        label: t.currentLeads.title,
        closeLabel: "Close Current leads",
        closeTitle: "Close Current",
        content: (
          <CurrentLeadsView
            frame={frame}
            onSelectNode={handleWorkspaceSelectNode}
            onToggleLeadStage={handleToggleLeadStage}
            readOnly={isSharedReadOnly}
          />
        )
      };
    }
    if (canonicalRegistryVisible) {
      return {
        label: t.workspace.canonicalRegistryAriaLabel,
        closeLabel: "Close canonical entity registry",
        closeTitle: "Close registry",
        content: (
          <CanonicalEntityRegistryPanel
            activeEntityId={activeCanonicalEntityId}
            count={canonicalRegistryQuery.data?.count ?? 0}
            enabled={canReadCanonicalRegistry}
            entityType={canonicalRegistryEntityType}
            error={canonicalRegistryQuery.error instanceof Error ? canonicalRegistryQuery.error.message : undefined}
            items={canonicalRegistryQuery.data?.items ?? []}
            onSelectEntity={handleCanonicalEntitySelect}
            pending={canReadCanonicalRegistry && canonicalRegistryQuery.isPending}
            query={searchTerm}
          />
        )
      };
    }
    // The Private Agent detail slot is the same entity detail plus the entities
    // this conversation loaded; the tabs own nothing else.
    if (isPrivateAgentWorkspace) {
      return {
        label: t.workspace.selectedEntityAriaLabel,
        closeLabel: "Close selected entity detail",
        closeTitle: "Close detail panel",
        content: (
          <PrivateAgentDetailTabs
            activeTab={privateAgentDetailTab}
            agentId={selectedAgentId}
            conversationId={search.privateAgentConversationId}
            detail={renderEntityDetail()}
            enabled={canUsePrivateAgent}
            onSelectEntity={handlePrivateAgentTouchedEntitySelect}
            onTabChange={setPrivateAgentDetailTab}
          />
        )
      };
    }
    return {
      label: t.workspace.selectedEntityAriaLabel,
      closeLabel: "Close selected entity detail",
      closeTitle: "Close detail panel",
      content: renderEntityDetail()
    };
  })();

  return (
    <WorkspaceShell
      ariaLabel={t.workspace.contactsIntelligenceWorkspaceAriaLabel}
      detailOpen={detailPanelOpen}
      privateAgent={isPrivateAgentWorkspace}
      header={<WorkspaceHeader
        activeView={activeView}
        availableAuthProviders={availableAuthProviders}
        frame={displayFrame}
        onAddClick={() => setAgentOpen(true)}
        onOpenBilling={handleOpenBilling}
        onOpenHelp={handleOpenOnboarding}
        onOpenSettings={handleOpenSettings}
        onSavedSearchSelect={handleSavedSearchSelect}
        onSearchKeyDown={handleGraphSearchKeyDown}
        onSearchQueryChange={setSearchQuery}
        onSelectNode={handleWorkspaceSelectNode}
        onSignIn={handleAuthProviderSignIn}
        onSignOut={handleAccountSignOut}
        onViewChange={handleWorkspaceViewChange}
        readOnly={isSharedReadOnly}
        savedSearchError={graphNodeSearchQuery.isError}
        savedSearchPending={graphNodeSearchQuery.isFetching}
        savedSearchResults={savedSearchResults}
        searchInputRef={searchInputRef}
        searchQuery={searchQuery}
        searchResults={searchResults}
        session={storedSession}
        showAddAction={!isPrivateAgentWorkspace}
        onToggleDetail={() => setDetailPanelOpen((open) => !open)}
        detailOpen={detailPanelOpen}
      />}
      body={isPrivateAgentWorkspace ? (
        <PrivateAgentWorkspace
          detailVisible={detailPanelOpen && !detailDrawer}
          key={selectedAgentId ?? "unselected"}
          agentId={selectedAgentId}
          agents={privateAgent.agents.map((agent) => agent.profile)}
          onAgentChange={setPrivateAgentSelection}
          placement={privateAgent.placement}
          agentError={privateAgent.agentsError ?? privateAgent.readinessError}
          onRefreshAgents={() => {
            privateAgent.refreshAgents();
          }}
          cancelingJobId={privateAgent.cancelingJobId}
          computerAvailability={privateAgentComputer}
          conversationId={search.privateAgentConversationId}
          devices={privateAgent.devices}
          enabled={canUsePrivateAgent}
          facet={privateAgentFacet}
          frame={frame}
          jobs={privateAgent.jobs}
          jobsError={privateAgent.jobsError}
          jobsLoading={privateAgent.jobsLoading}
          materializeError={privateAgent.materializeError}
          materializingJobId={privateAgent.materializingJobId}
          onCancel={privateAgent.cancelJob}
          onConversationChange={setPrivateAgentConversation}
          onFacetChange={setPrivateAgentFacet}
          onMaterialize={privateAgent.materializeJob}
          onOpenComputers={() => handleOpenSettingsSection("computers")}
          onOpenPrivateAgentSettings={() => handleOpenSettingsSection("private-agent")}
          profile={privateAgent.profile}
          profileLoading={privateAgent.profileLoading}
          profileMissing={privateAgent.profileMissing}
          readiness={privateAgent.readiness}
        />
      ) : (
        <>
      <LeftRail
        activeFrameRecordId={lastResult?.commit.id ?? null}
        activeThreadId={threadId}
        filters={filters}
        frame={displayFrame}
        frameItems={isSharedReadOnly ? [] : frameListQuery.data?.items ?? []}
        frameListError={isSharedReadOnly ? false : frameListQuery.isError}
        frameListPending={isSharedReadOnly ? false : frameListQuery.isPending}
        onDeleteThread={handleDeleteThread}
        onNewThread={handleNewThread}
        onSelectFrameRecord={handleSelectFrameRecord}
        onSelectThread={handleSelectThread}
        onToggleFilter={(entityType) => {
          setActiveView("graph");
          setFilters((current) => ({
            ...current,
            [entityType]: !current[entityType]
          }));
        }}
        restoreErrorFrameId={
          sharedFrameQuery.isError && search.share
            ? "shared link"
            : frameRestoreQuery.isError && search.frame
              ? search.frame
              : null
        }
        showFrameHistory={!isSharedReadOnly}
        showThreadHistory={!isSharedReadOnly}
        threadItems={isSharedReadOnly ? [] : threadListQuery.data?.threads ?? []}
        threadListError={isSharedReadOnly ? false : threadListQuery.isError}
        threadListPending={isSharedReadOnly ? false : threadListQuery.isPending}
        threadRestoreErrorId={isSharedReadOnly ? null : activeThreadRestoreErrorId}
      />

      <GraphStage
        canSaveTimelineReplay={!isSharedReadOnly && Boolean(mutableFrameRecordId) && Boolean(timelineReplayChangeSet)}
        changeSetCreatePending={createChangeSetPending}
        changeSetState={changeSetState}
        enabledEntityTypes={enabledEntityTypes}
        frame={displayFrame}
        frameIoState={frameIoState}
        frameSharePending={frameSharePending}
        onApplyChangeSet={applyChangeSet}
        onDismissChangeSetStatus={dismissChangeSetLifecycle}
        onDismissFrameIoStatus={dismissFrameIoStatus}
        onFrameShareClick={handleFrameShareClick}
        onHelpClick={handleOpenOnboarding}
        onRecenter={handleWorkspaceRecenter}
        onRevertChangeSet={revertChangeSet}
        onSettingsClick={handleOpenSettings}
        onSelectEdge={activeView === "overview" ? () => undefined : handleWorkspaceSelectEdge}
        onSelectNode={activeView === "overview" ? handleOverviewNodeSelect : handleWorkspaceSelectNode}
        onSaveTimelineReplay={handleSaveTimelineReplay}
        onTimelineFocusChange={setTimelineFocus}
        onTimelineNeedsEventAction={() => setAgentOpen(true)}
        onViewModeChange={handleViewModeChange}
        onZoomChange={setClampedZoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        readOnly={isSharedReadOnly}
        searchResultIds={activeView === "overview" ? [] : searchResultIds}
        selectedNode={activeView === "overview" ? null : selectedNode}
        selectedEdgeId={activeView === "overview" ? null : selectedEdgeId}
        spotlightNodeIds={activeView === "overview" ? [] : spotlightNodeIds}
        timeline={timeline}
        timelineFocus={timelineFocus}
        viewMode={viewMode}
        zoomLevel={zoomLevel}
      />

        </>
      )}
      detail={<WorkspaceSidePanel
          id="workspace-detail"
          triggerId="workspace-detail-trigger"
          drawer={detailIsDrawer}
          open={detailPanelOpen}
          onClose={() => setDetailPanelOpen(false)}
          className="detail-panel"
          label={detailSurface.label}
        >
          {/* Inline the panel is closed from the header toggle; only the modal
              drawer needs a close control of its own. */}
          {detailIsDrawer ? (
          <button
            className="detail-close-button icon-button"
            type="button"
            aria-label={detailSurface.closeLabel}
            title={detailSurface.closeTitle}
            onClick={() => setDetailPanelOpen(false)}
          >
            <X size={17} />
          </button>
          ) : null}
          {isPrivateAgentWorkspace && searchSelectionStatus ? (
            <div role={searchSelectionStatus.tone}>
              {searchSelectionStatus.message === "unavailable" ? t.workspace.unavailable : t.common.loading}
              {searchSelectionStatus.retry ? <button type="button" className="secondary-button" onClick={() => { void frameRestoreQuery.refetch(); }}>{t.common.retry}</button> : null}
            </div>
          ) : null}
          {detailSurface.content}
      </WorkspaceSidePanel>}
      overlays={<>
      {!isPrivateAgentWorkspace ? <AgentOverlay
        activeMentionIndex={activeMentionIndex}
        agentAuthLabel={agentAuthLabel}
        agentOpen={agentOpen}
        agentThreadRef={agentThreadRef}
        availableAuthProviders={availableAuthProviders}
        chatMessages={chatMessages}
        frame={frame}
        handoff={
          // The Generic Agent does not run research; it hands the selected entity
          // to the Private Agent's own trigger, which owns every admission check.
          researchTargetNode && privateAgentName && !isSharedReadOnly
            ? {
                agentName: privateAgentName,
                onHandoff: () => {
                  setAgentOpen(false);
                  handleWorkspaceViewChange("graph");
                  setDetailPanelOpen(true);
                }
              }
            : null
        }
        isGenerating={mutation.isPending}
        mentionCandidates={mentionCandidates}
        mentionQuery={mentionQuery}
        message={message}
        mode={agentMode}
        mutationError={mutationError ?? threadSaveError}
        onAuthProviderSignIn={handleAuthProviderSignIn}
        onClose={() => setAgentOpen(false)}
        onMentionSelect={insertMention}
        onMessageChange={handleMessageChange}
        onMessageCursorChange={handleMessageCursorChange}
        onMessageKeyDown={handleMessageKeyDown}
        onMessageKeyUp={handleMessageKeyUp}
        onModeChange={setAgentMode}
        onOpen={() => setAgentOpen(true)}
        onSubmit={handleSubmit}
        readOnly={isSharedReadOnly}
        sampleText={sampleText}
        onUseSample={handleUseSample}
        storedSession={storedSession}
        textareaRef={textareaRef}
      /> : null}
      {transactionGraphDialog}
      </>}
    />
  );
}

function chatMessageId(prefix: string) {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
}

function assistantContentFor(result: AgentChatResult) {
  if (result.assistantContent.trim().length > 0) {
    return result.assistantContent;
  }

  if (result.mode === "seeker") {
    return "Seeker did not return any discussion text for this turn.";
  }

  const reviewText =
    result.commit.status === "needs_review"
      ? "I generated a graph frame that needs review before it is persisted."
      : "I generated and committed the graph frame.";
  return `${reviewText} ${result.frame.summary}`;
}

// Contract AgentChatMessage -> local (UI-status-bearing) AgentChatMessage. Resumed
// messages are always historical, so they render as already completed.
function hydrateAgentChatMessages(messages: AgentChatThreadMessage[]): AgentChatMessage[] {
  return messages.map((message) => ({
    id: message.id,
    role: message.role,
    content: message.content,
    status: "done"
  }));
}

// Local AgentChatMessage -> contract AgentChatMessage for the thread upsert body.
// Drops pending/errored/empty entries: AgentChatThreadUpsertRequestSchema requires
// non-empty content on every message, and a half-finished turn is not a saveable
// transcript. Returns [] when there is no complete user+assistant exchange yet, and
// the caller skips the save in that case.
function agentChatThreadUpsertMessages(messages: AgentChatMessage[]): AgentChatThreadMessage[] {
  const persistable = messages.filter(
    (message) => message.content.trim().length > 0 && (message.role === "user" || message.status === "done")
  );
  const hasUserMessage = persistable.some((message) => message.role === "user");
  const hasCompletedAssistantMessage = persistable.some(
    (message) => message.role === "assistant" && message.status === "done"
  );

  if (!hasUserMessage || !hasCompletedAssistantMessage) {
    return [];
  }

  return persistable.map((message) => ({
    id: message.id,
    role: message.role,
    content: message.content,
    mentions: []
  }));
}

async function resolveNoteIntakeCommit(
  response: GraphNoteIntakeResponse,
  onStatus: (content: string) => void
): Promise<{ commit: GraphFrameCommitResponse; frame: GraphVisualizationFrame }> {
  if (!response.queued) {
    return {
      commit: response.commit,
      frame: response.frame
    };
  }

  onStatus("Queued note intake. Waiting for the graph frame commit.");
  for (let attempt = 0; attempt < noteIntakePollAttempts; attempt += 1) {
    await delay(noteIntakePollIntervalMs);
    const job = await getNoteIntakeJob(response.intakeId);

    if (job.status === "failed") {
      throw new Error(job.failureMessage ?? "Note intake failed before commit.");
    }

    if (job.status === "processing") {
      onStatus("Processing note intake.");
    }

    if (job.status === "committed" && job.frameRecordId) {
      onStatus("Note intake committed. Restoring graph frame.");
      const record = await getGraphFrame(job.frameRecordId);
      return {
        commit: commitFromFrameRecord(record),
        frame: record.frame
      };
    }
  }

  throw new Error("Note intake is still queued. Try again after the job finishes.");
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

// A restored record was committed earlier, so its projection is the steady-state
// "projected" — the record itself does not carry a per-read projection status.
function commitFromFrameRecord(record: GraphFrameRecord): GraphFrameCommitResponse {
  return {
    id: record.id,
    status: record.status,
    acceptedAt: record.acceptedAt,
    nextAction: record.nextAction,
    // Preserve the committed frame version so downstream features (e.g. relationship
    // research `expectedFrameVersion`) read a real version instead of guessing.
    ...(record.version === undefined ? {} : { version: record.version }),
    projectionStatus: "projected"
  };
}

function statusTextForAgentStream(status: string, t: Dictionary) {
  switch (status) {
    case "tanstack_ai_generate_starting":
      return t.agentChat.streamStructuring;
    case "tanstack_ai_provider_auth_required":
    case "tanstack_ai_provider_unconfigured":
      return t.agentChat.streamAuthRequired;
    case "tanstack_ai_seeker_starting":
      return t.agentChat.streamSeekerStarting;
    default:
      return t.agentChat.streamRebuilding;
  }
}

function findActiveMention(value: string, cursor: number): MentionQuery | null {
  const beforeCursor = value.slice(0, cursor);
  const match = beforeCursor.match(/(^|[\s([，,。；;])@([^\s@]*)$/);
  if (!match) {
    return null;
  }

  const text = match[2] ?? "";
  return {
    start: cursor - text.length - 1,
    end: cursor,
    text
  };
}

function mentionCandidateScore(node: GraphNode, term: string) {
  if (term.length === 0) {
    return 1;
  }

  const label = node.label.toLowerCase();
  if (label.startsWith(term)) {
    return 5;
  }
  if (label.includes(term)) {
    return 4;
  }
  if (node.type.includes(term)) {
    return 3;
  }
  if (graphSearchText(node).includes(term)) {
    return 2;
  }

  return 0;
}

function workspaceFiltersForView(viewId: string): Record<GraphEntityType, boolean> {
  switch (viewId) {
    case "people":
      return entityFilterPreset("person");
    case "companies":
      return entityFilterPreset("organisation");
    case "events":
      return entityFilterPreset("event");
    case "signals":
      return entityFilterPreset("opportunity");
    default:
      return {
        event: true,
        note: true,
        opportunity: true,
        organisation: true,
        person: true
      };
  }
}

function entityFilterPreset(entityType: GraphEntityType): Record<GraphEntityType, boolean> {
  return {
    event: entityType === "event",
    note: false,
    opportunity: entityType === "opportunity",
    organisation: entityType === "organisation",
    person: entityType === "person"
  };
}

function mentionTokenFor(node: GraphNode) {
  return `@${node.label}`;
}
